import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './components/providers/AuthProvider';
import { AuthDialogProvider } from './contexts/AuthDialogContext';
import { ToastProvider } from './contexts/ToastContext';
import { CartProvider } from './contexts/CartContext';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { MembershipProvider } from './contexts/MembershipContext';
import NetworkProvider from './components/providers/NetworkProvider';
import './index.css';
import App from './App.tsx';
import i18n from './translate.ts';
import { guestSessionManager } from './utils/guestSession';
import { preloadCriticalResources } from './utils/resourcePreloader';
import logger from './utils/logger';
import { initializeCSSOptimizations } from './utils/cssOptimization.ts';
import { initializeJSOptimizations } from './utils/jsOptimization.ts';
import { initializeResourceHints } from './utils/resourceHints.ts';
import { beginDeployRecovery, finalizeDeployRecoveryBootstrap } from './utils/deployRecovery.ts';
import { bootstrapAffiliateAttributionFromUrl } from './utils/affiliateAttribution.ts';

const scheduleNonCriticalTask = (task: () => void, timeout = 5000) => {
  const runTask = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback?.(task, { timeout });
    } else {
      window.setTimeout(task, 0);
    }
  };

  if (document.readyState === 'complete') {
    runTask();
    return;
  }

  window.addEventListener('load', runTask, { once: true });
};

const initializeSentry = async () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    return;
  }

  try {
    const Sentry = await import('@sentry/react');

    Sentry.init({
      dsn,
      integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
      tracesSampleRate: 1.0,
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/core\.myeztopup\.com/,
        /^https:\/\/stagingcore\.myeztopup\.com/,
        /^https:\/\/devcore\.myeztopup\.com/,
      ],
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    });
  } catch (error) {
    logger.warn('Sentry initialization failed:', error);
  }
};

// Global error handlers for unhandled errors
const setupGlobalErrorHandlers = () => {
  // Handle unhandled JavaScript errors
  window.addEventListener('error', (event) => {
    const runtimeError =
      event.error instanceof Error
        ? event.error
        : new Error(event.message || 'Unhandled runtime error');

    if (beginDeployRecovery(runtimeError, 'window.error')) {
      event.preventDefault();
      return;
    }

    logger.error('🚨 Unhandled Error:', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error,
      stack: event.error?.stack,
    });

    // Prevent the error from causing a white screen
    event.preventDefault();

    // Log to external service in production
    if (process.env.NODE_ENV === 'production') {
      try {
        logger.log('📊 Unhandled error logged for production monitoring');
        // You can integrate with error tracking services here
        // window.errorTracker?.captureException(event.error);
      } catch (loggingError) {
        logger.error('Failed to log unhandled error:', loggingError);
      }
    }
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const rejectionReason =
      event.reason instanceof Error
        ? event.reason
        : new Error(
            typeof event.reason === 'string' ? event.reason : 'Unhandled promise rejection',
          );

    if (beginDeployRecovery(rejectionReason, 'window.unhandledrejection')) {
      event.preventDefault();
      return;
    }

    logger.error('🚨 Unhandled Promise Rejection:', {
      reason: event.reason,
      promise: event.promise,
    });

    // Prevent the error from causing a white screen
    event.preventDefault();

    // Log to external service in production
    if (process.env.NODE_ENV === 'production') {
      try {
        logger.log('📊 Unhandled promise rejection logged for production monitoring');
        // You can integrate with error tracking services here
        // window.errorTracker?.captureException(event.reason);
      } catch (loggingError) {
        logger.error('Failed to log unhandled promise rejection:', loggingError);
      }
    }
  });

  logger.log('✅ Global error handlers initialized');
};

finalizeDeployRecoveryBootstrap();

// Initialize global error handlers
setupGlobalErrorHandlers();
bootstrapAffiliateAttributionFromUrl();

// Expose guestSessionManager for debugging
(window as unknown as { guestSessionManager: typeof guestSessionManager }).guestSessionManager =
  guestSessionManager;

// Register service worker for caching and performance
if (
  'serviceWorker' in navigator &&
  (import.meta.env.PROD ||
    import.meta.env.MODE === 'staging' ||
    import.meta.env.MODE === 'development' ||
    import.meta.env.MODE === 'production')
) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', {
        scope: '/',
        updateViaCache: 'imports',
      })
      .then((registration) => {
        logger.log('SW registered: ', registration);

        // Check for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New service worker is available
                logger.log('New service worker available');
              }
            });
          }
        });
      })
      .catch((registrationError) => {
        logger.log('SW registration failed: ', registrationError);
      });
  });
}

// Performance optimizations for TBT
const optimizePerformance = async () => {
  // Defer heavy library imports
  (window as unknown as { lazyImports: Record<string, () => Promise<unknown>> }).lazyImports = {
    lottie: () => import('lottie-react'),
    colorThief: () => import('colorthief'),
    qrCode: () => import('qrcode.react'),
    html2canvas: () => import('html2canvas'),
  };

  // Preload critical resources for better LCP
  try {
    initializeResourceHints();
    await preloadCriticalResources();
    initializeCSSOptimizations();
    initializeJSOptimizations();
    logger.log('✅ Critical resources preloaded successfully');
  } catch (error) {
    logger.warn('⚠️ Some critical resources failed to preload:', error);
  }
};

// Call optimization function
// Initialize performance optimizations
optimizePerformance().catch((error) => {
  logger.warn('Performance optimization failed:', error);
});

scheduleNonCriticalTask(() => {
  void initializeSentry();
});

const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <GoogleOAuthProvider clientId={googleClientId}>
      <I18nextProvider i18n={i18n}>
        <BrowserRouter>
          <AnalyticsProvider>
            <NetworkProvider>
              <ToastProvider>
                <AuthProvider>
                  <AuthDialogProvider>
                    <CartProvider>
                      <MembershipProvider>
                        <App />
                      </MembershipProvider>
                    </CartProvider>
                  </AuthDialogProvider>
                </AuthProvider>
              </ToastProvider>
            </NetworkProvider>
          </AnalyticsProvider>
        </BrowserRouter>
      </I18nextProvider>
    </GoogleOAuthProvider>
  </StrictMode>,
);
